import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  BinanceDeposit,
  BinanceDepositService,
  ErrorConfirmarDeposito,
} from '../../../services/binance-deposit.service';
import { UserService } from '../../../services/user.service';
import { Usuario } from '../../../interfaces/transaccion.interface';
import { BalanceSearchFilter } from 'src/app/shared/enums/balance-search-filter.enum';

export interface AsignarDepositoDialogData {
  deposito: BinanceDeposit;
}

/**
 * Largo máximo del motivo. El API lo deja solo en su log (`[ASIGNACION
 * DEPOSITO]`); el cardex del cliente dice «Depósito confirmado por soporte».
 */
export const MAX_MOTIVO_ASIGNACION = 200;

/** Texto cuando la asignación quedó sin respuesta clara (sin conexión o 5xx). */
export const TEXTO_ASIGNACION_INCIERTA =
  'No hubo una respuesta clara del servidor. Actualiza la lista antes de reintentar: si el depósito ya figura asignado, no lo repitas.';

/**
 * ¿La fila admite «Asignar a cliente»? Mismas condiciones con que el API
 * acepta el confirm: SUCCESS en Binance, sin dueño, sin abonar y sin estado
 * interno.
 */
export function puedeAsignarDeposito(dep: Partial<BinanceDeposit> | null | undefined): boolean {
  return (
    !!dep &&
    dep.binanceStatus === 'SUCCESS' &&
    !dep.userId &&
    !dep.abonada &&
    !dep.internalStatus
  );
}

/** El listado del API enmascara el txId: deja solo los últimos 3 caracteres. */
export function sufijoVisible(txIdEnmascarado: string | null | undefined): string {
  return String(txIdEnmascarado ?? '').replace(/^\*+/, '');
}

/** Igual que el API: «Off-chain transfer 123…» se queda con el último token. */
export function normalizarTxId(valor: string | null | undefined): string {
  const t = String(valor ?? '').trim();
  return t.startsWith('Off-chain transfer') ? t.split(/\s+/).pop() ?? '' : t;
}

/**
 * ¿El txId que ingresa el operador es el de esta fila? El enmascarado
 * conserva el largo original y los últimos 3 caracteres: los dos tienen que
 * calzar. Evita asignar por error un depósito distinto al de la fila.
 */
export function txIdCalzaConFila(ingresado: string, enmascarado: string): boolean {
  const tx = normalizarTxId(ingresado);
  const mascara = String(enmascarado ?? '');
  if (!tx || !mascara) return false;
  if (!mascara.startsWith('*')) return tx === mascara;
  return tx.length === mascara.length && tx.endsWith(sufijoVisible(mascara));
}

/** Nombre a mostrar de un cliente (persona o empresa). */
export function nombreCliente(u: Partial<Usuario> & { razonSocial?: string } | null | undefined): string {
  if (!u) return '';
  const persona = `${u.nombre ?? ''} ${u.apellidos ?? ''}`.trim();
  return persona || u.razonSocial || u.email || `Usuario ${u.usuarioId ?? ''}`.trim();
}

/**
 * «Asignar a cliente» (25/10/2026): el operador acredita un depósito USDT sin
 * dueño a un cliente. Hace falta para lo que el cliente ya no puede reclamar
 * solo (más de 30.000 USDT o más de 72 h desde la llegada). Usa el mismo
 * `POST binance-deposits/confirm`: para un operador el API toma el `userId`
 * del body, sin tope ni ventana. El cliente ve «Depósito confirmado por
 * soporte»; el operador y el motivo quedan solo en el log del API.
 */
@Component({
  selector: 'app-asignar-deposito-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './asignar-deposito-dialog.component.html',
  styleUrls: ['./asignar-deposito-dialog.component.sass'],
})
export class AsignarDepositoDialogComponent {
  readonly maxMotivo = MAX_MOTIVO_ASIGNACION;
  readonly filtros = [
    { valor: BalanceSearchFilter.EMAIL, texto: 'Email' },
    { valor: BalanceSearchFilter.ID, texto: 'ID' },
    { valor: BalanceSearchFilter.DOCUMENT, texto: 'Documento' },
  ];

  filtro: string = BalanceSearchFilter.EMAIL;
  busqueda = '';
  buscando = false;
  buscado = false;
  resultados: Usuario[] = [];
  errorBusqueda = '';
  cliente: Usuario | null = null;

  txId = '';
  motivo = '';
  enviando = false;
  error = '';
  /** Hubo un error sin respuesta clara: al cerrar se refresca la lista igual. */
  incierto = false;
  /** La fila cambió de estado (409, p. ej. ya asignado): al cerrar se refresca. */
  filaDesactualizada = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AsignarDepositoDialogData,
    private dialogRef: MatDialogRef<AsignarDepositoDialogComponent, boolean>,
    private depositos: BinanceDepositService,
    private usuarios: UserService,
  ) {
    // Si el listado no lo enmascaró (txId corto), queda precargado.
    const tx = String(data.deposito?.txId ?? '');
    if (tx && !tx.startsWith('*')) this.txId = tx;
  }

  get deposito(): BinanceDeposit {
    return this.data.deposito;
  }

  get sufijo(): string {
    return sufijoVisible(this.deposito.txId);
  }

  get txIdValido(): boolean {
    return txIdCalzaConFila(this.txId, this.deposito.txId);
  }

  get motivoValido(): boolean {
    const m = this.motivo.trim();
    return m.length >= 5 && m.length <= this.maxMotivo;
  }

  get puedeConfirmar(): boolean {
    return !!this.cliente && this.txIdValido && this.motivoValido && !this.enviando;
  }

  nombre(u: Usuario | null): string {
    return nombreCliente(u as any);
  }

  async buscar(): Promise<void> {
    let valor = this.busqueda.trim();
    if (!valor || this.buscando) return;
    if (this.filtro === BalanceSearchFilter.DOCUMENT) {
      valor = valor.replace(/[.\-]/g, '');
    }
    if (this.filtro === BalanceSearchFilter.ID && !/^\d+$/.test(valor)) {
      this.errorBusqueda = 'El ID es un número.';
      return;
    }
    this.buscando = true;
    this.errorBusqueda = '';
    this.cliente = null;
    try {
      // Igual que el buscador de saldos del panel (users-balance-search).
      const res = await this.usuarios.searchUsers(valor, this.filtro.toUpperCase());
      this.resultados = Array.isArray(res) ? res.slice(0, 20) : [];
      if (this.resultados.length === 1) this.cliente = this.resultados[0];
    } catch (e: any) {
      this.resultados = [];
      this.errorBusqueda = e?.error?.message || e?.message || 'No se pudo buscar el cliente.';
    } finally {
      this.buscando = false;
      this.buscado = true;
    }
  }

  elegir(u: Usuario): void {
    if (this.enviando) return;
    this.cliente = u;
  }

  confirmar(): void {
    if (!this.puedeConfirmar || !this.cliente) return;
    this.enviando = true;
    this.error = '';
    this.depositos
      .confirmDeposit({
        userId: String(this.cliente.usuarioId),
        txId: normalizarTxId(this.txId),
        network: this.deposito.network,
        motivo: this.motivo.trim(),
        // El txId solo se valida por largo y últimos 3: el API confirma que
        // sea el de esta fila (409 si apunta a otro depósito).
        depositId: this.deposito.id,
      })
      .subscribe({
        next: () => {
          this.enviando = false;
          this.dialogRef.close(true);
        },
        error: (e: ErrorConfirmarDeposito) => {
          this.enviando = false;
          const incierto = e?.status === 0 || (typeof e?.status === 'number' && e.status >= 500);
          if (incierto) this.incierto = true;
          if (e?.status === 409) this.filaDesactualizada = true;
          this.error = incierto ? TEXTO_ASIGNACION_INCIERTA : e?.message || 'No se pudo asignar el depósito.';
        },
      });
  }

  cerrar(): void {
    if (this.enviando) return;
    this.dialogRef.close(this.incierto || this.filaDesactualizada);
  }
}
